/**
 * F4D Quick Add - Inline and Modal Quick Add Components
 * Provides quick add-to-cart functionality for product cards
 */

(function() {
  'use strict';

  // Cache for product data
  const productCache = new Map();

  /**
   * Inline Quick Add Component
   * Handles single-variant products with +/- quantity controls
   */
  class F4DQuickAddInline extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.productHandle = this.dataset.productHandle;
      this.productUrl = this.dataset.productUrl;
      this.isSingleVariant = this.dataset.singleVariant === 'true';
      this.quantity = 1;
    }

    connectedCallback() {
      this.setupEventListeners();
    }

    disconnectedCallback() {
      // Cleanup if needed
    }

    setupEventListeners() {
      // Quantity buttons
      const decreaseBtn = this.querySelector('[data-action="decrease"]');
      const increaseBtn = this.querySelector('[data-action="increase"]');
      const addToCartBtn = this.querySelector('[data-action="add-to-cart"]');
      const openModalBtn = this.querySelector('[data-action="open-modal"]');

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.updateQuantity(-1);
        });
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.updateQuantity(1);
        });
      }

      if (addToCartBtn) {
        addToCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.addToCart();
        });
      }

      if (openModalBtn) {
        openModalBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openModal();
        });
      }
    }

    updateQuantity(change) {
      const input = this.querySelector('.f4d-quick-add-inline__qty-input');
      if (!input) return;

      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 99;
      const newValue = Math.max(min, Math.min(max, this.quantity + change));

      this.quantity = newValue;
      input.value = newValue;

      // Update button states
      const decreaseBtn = this.querySelector('[data-action="decrease"]');
      const increaseBtn = this.querySelector('[data-action="increase"]');

      if (decreaseBtn) {
        decreaseBtn.disabled = newValue <= min;
      }
      if (increaseBtn) {
        increaseBtn.disabled = newValue >= max;
      }
    }

    async addToCart() {
      const addBtn = this.querySelector('[data-action="add-to-cart"]');
      if (!addBtn || addBtn.disabled) return;

      // Set loading state
      addBtn.dataset.state = 'loading';
      addBtn.disabled = true;

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: this.variantId,
            quantity: this.quantity
          })
        });

        const data = await response.json();

        if (data.status === 422 || data.status === 'error') {
          throw new Error(data.message || 'Could not add to cart');
        }

        // Success
        addBtn.dataset.state = 'success';

        // Update cart count in header
        this.updateCartCount();

        // Dispatch cart update event
        this.dispatchCartEvent(data);

        // Reset after delay
        setTimeout(() => {
          addBtn.dataset.state = '';
          addBtn.disabled = false;
          this.quantity = 1;
          const input = this.querySelector('.f4d-quick-add-inline__qty-input');
          if (input) input.value = 1;
        }, 2000);

      } catch (error) {
        console.error('Add to cart error:', error);
        addBtn.dataset.state = '';
        addBtn.disabled = false;
        // Show error feedback
        this.showError(error.message);
      }
    }

    async openModal() {
      const modal = document.querySelector('#f4d-quick-add-modal');
      if (!modal) return;

      modal.open(this.productHandle, this.productId, this.productUrl);
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Update cart icon count
        const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count], .header-actions__cart-count');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.classList.remove('hidden');
          }
        });

        // Try to update cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.refresh === 'function') {
          cartDrawer.refresh();
        }
      } catch (e) {
        console.error('Could not update cart count', e);
      }
    }

    dispatchCartEvent(data) {
      // Dispatch custom event for other components to listen to
      document.dispatchEvent(new CustomEvent('cart:add', {
        detail: {
          product_id: this.productId,
          variant_id: this.variantId,
          quantity: this.quantity,
          cart: data
        }
      }));

      // Also dispatch the theme's native cart update event if available
      if (typeof ThemeEvents !== 'undefined') {
        document.dispatchEvent(new CustomEvent(ThemeEvents?.cartUpdate || 'theme:cart:update', {
          detail: { resource: data }
        }));
      }
    }

    showError(message) {
      // Could show a toast notification here
      console.error('Quick Add Error:', message);
    }
  }

  /**
   * Modal Quick Add Component
   * Handles multi-variant products with variant selection
   */
  class F4DQuickAddModal extends HTMLElement {
    constructor() {
      super();
      this.productData = null;
      this.selectedVariant = null;
      this.quantity = 1;
      this.selectedOptions = {};
    }

    connectedCallback() {
      this.setupEventListeners();
    }

    setupEventListeners() {
      // Close button and backdrop
      const closeButtons = this.querySelectorAll('[data-action="close"]');
      closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Quantity buttons
      const decreaseBtn = this.querySelector('.f4d-quick-add-modal__qty-btn--minus');
      const increaseBtn = this.querySelector('.f4d-quick-add-modal__qty-btn--plus');

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => this.updateQuantity(-1));
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => this.updateQuantity(1));
      }

      // Add to cart button
      const addToCartBtn = this.querySelector('[data-action="add-to-cart"]');
      if (addToCartBtn) {
        addToCartBtn.addEventListener('click', () => this.addToCart());
      }

      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      });
    }

    async open(productHandle, productId, productUrl) {
      // Show modal with loading state
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      const loadingEl = this.querySelector('.f4d-quick-add-modal__loading');
      const productEl = this.querySelector('.f4d-quick-add-modal__product');

      if (loadingEl) loadingEl.style.display = 'flex';
      if (productEl) productEl.style.display = 'none';

      // Reset state
      this.quantity = 1;
      this.selectedOptions = {};
      this.selectedVariant = null;

      try {
        // Fetch product data
        this.productData = await this.fetchProductData(productHandle, productUrl);

        // Render product content
        this.renderProduct();

        // Hide loading, show product
        if (loadingEl) loadingEl.style.display = 'none';
        if (productEl) productEl.style.display = 'grid';

      } catch (error) {
        console.error('Error loading product:', error);
        this.close();
      }
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Reset add button state
      const addBtn = this.querySelector('[data-action="add-to-cart"]');
      if (addBtn) {
        addBtn.dataset.state = '';
        addBtn.disabled = false;
      }
    }

    async fetchProductData(handle, productUrl) {
      // Check cache first
      if (productCache.has(handle)) {
        return productCache.get(handle);
      }

      const productPath = productUrl ? productUrl.split('?')[0] : null;
      const url = productPath ? `${productPath}.js` : `/products/${handle}.js`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Product not found');
      }

      const data = await response.json();
      productCache.set(handle, data);
      return data;
    }

    renderProduct() {
      if (!this.productData) return;

      const product = this.productData;

      // Set image
      const imageEl = this.querySelector('.f4d-quick-add-modal__image');
      if (imageEl && product.featured_image) {
        imageEl.src = this.getSizedImageUrl(product.featured_image, '400x');
        imageEl.alt = product.title;
      }

      // Set title
      const titleEl = this.querySelector('.f4d-quick-add-modal__title');
      if (titleEl) {
        titleEl.textContent = product.title;
      }

      // Set view product link
      const linkEl = this.querySelector('.f4d-quick-add-modal__view-product');
      if (linkEl) {
        linkEl.href = product.url;
      }

      // Render variants
      this.renderVariants();

      // Select first available variant
      const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
      if (firstAvailable) {
        this.selectVariant(firstAvailable);
      }

      // Update price
      this.updatePrice();

      // Reset quantity
      const qtyInput = this.querySelector('.f4d-quick-add-modal__qty-input');
      if (qtyInput) {
        qtyInput.value = 1;
        this.quantity = 1;
      }
    }

    renderVariants() {
      const variantsContainer = this.querySelector('.f4d-quick-add-modal__variants');
      if (!variantsContainer || !this.productData) return;

      const product = this.productData;

      // Clear existing
      variantsContainer.innerHTML = '';

      // If only one variant and it's the default, don't show options
      if (product.variants.length === 1) {
        return;
      }

      // Render each option
      // Note: product.options can be array of strings or array of objects with name property
      product.options.forEach((option, optionIndex) => {
        const optionName = typeof option === 'string' ? option : (option.name || option);
        const optionValues = this.getUniqueOptionValues(optionIndex);

        const groupEl = document.createElement('div');
        groupEl.className = 'f4d-quick-add-modal__variant-group';

        const labelEl = document.createElement('label');
        labelEl.className = 'f4d-quick-add-modal__variant-label';
        labelEl.textContent = optionName;

        const optionsEl = document.createElement('div');
        optionsEl.className = 'f4d-quick-add-modal__variant-options';
        optionsEl.dataset.optionIndex = optionIndex;

        optionValues.forEach(value => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'f4d-quick-add-modal__variant-btn';
          btn.textContent = value;
          btn.dataset.value = value;
          btn.dataset.optionIndex = optionIndex;

          btn.addEventListener('click', () => this.selectOption(optionIndex, value));

          optionsEl.appendChild(btn);
        });

        groupEl.appendChild(labelEl);
        groupEl.appendChild(optionsEl);
        variantsContainer.appendChild(groupEl);
      });
    }

    getUniqueOptionValues(optionIndex) {
      const values = new Set();
      this.productData.variants.forEach(variant => {
        const optionValue = variant.options[optionIndex];
        if (optionValue) {
          values.add(optionValue);
        }
      });
      return Array.from(values);
    }

    selectOption(optionIndex, value) {
      // Update selected options
      this.selectedOptions[optionIndex] = value;

      // Update button states
      const buttons = this.querySelectorAll(`[data-option-index="${optionIndex}"]`);
      buttons.forEach(btn => {
        btn.dataset.selected = btn.dataset.value === value ? 'true' : 'false';
      });

      // Find matching variant
      this.findMatchingVariant();

      // Update availability of other options
      this.updateOptionAvailability();

      // Update price
      this.updatePrice();
    }

    findMatchingVariant() {
      if (!this.productData) return;

      const selectedOptionsCount = Object.keys(this.selectedOptions).length;
      const totalOptions = this.productData.options.length;

      // Only match if all options are selected
      if (selectedOptionsCount !== totalOptions) {
        this.selectedVariant = null;
        return;
      }

      // Find variant that matches all selected options
      this.selectedVariant = this.productData.variants.find(variant => {
        return this.productData.options.every((option, index) => {
          return variant.options[index] === this.selectedOptions[index];
        });
      });

      // Update add to cart button state
      const addBtn = this.querySelector('[data-action="add-to-cart"]');
      if (addBtn) {
        addBtn.disabled = !this.selectedVariant || !this.selectedVariant.available;
      }
    }

    updateOptionAvailability() {
      // Update which options are available based on current selection
      const product = this.productData;
      if (!product) return;

      product.options.forEach((optionName, optionIndex) => {
        const buttons = this.querySelectorAll(`.f4d-quick-add-modal__variant-options[data-option-index="${optionIndex}"] .f4d-quick-add-modal__variant-btn`);

        buttons.forEach(btn => {
          const value = btn.dataset.value;

          // Check if any variant with this option value is available
          const isAvailable = product.variants.some(variant => {
            if (variant.options[optionIndex] !== value) return false;
            if (!variant.available) return false;

            // Check if it matches other selected options
            return Object.entries(this.selectedOptions).every(([idx, selectedValue]) => {
              const index = parseInt(idx);
              if (index === optionIndex) return true; // Skip current option
              return variant.options[index] === selectedValue;
            });
          });

          btn.disabled = !isAvailable;
        });
      });
    }

    selectVariant(variant) {
      if (!variant) return;

      this.selectedVariant = variant;

      // Update selected options from variant
      variant.options.forEach((value, index) => {
        this.selectedOptions[index] = value;
      });

      // Update UI
      variant.options.forEach((value, index) => {
        const buttons = this.querySelectorAll(`[data-option-index="${index}"]`);
        buttons.forEach(btn => {
          btn.dataset.selected = btn.dataset.value === value ? 'true' : 'false';
        });
      });

      // Update button state
      const addBtn = this.querySelector('[data-action="add-to-cart"]');
      if (addBtn) {
        addBtn.disabled = !variant.available;
      }

      // Update availability
      this.updateOptionAvailability();
    }

    updatePrice() {
      const priceEl = this.querySelector('.f4d-quick-add-modal__price');
      if (!priceEl) return;

      const variant = this.selectedVariant;
      const product = this.productData;

      if (variant) {
        const price = this.formatMoney(variant.price);
        const comparePrice = variant.compare_at_price > variant.price
          ? this.formatMoney(variant.compare_at_price)
          : null;

        priceEl.innerHTML = comparePrice
          ? `<span class="compare-price">${comparePrice}</span> ${price}`
          : price;

        // Update image if variant has one
        if (variant.featured_image) {
          const imageEl = this.querySelector('.f4d-quick-add-modal__image');
          if (imageEl) {
            imageEl.src = this.getSizedImageUrl(variant.featured_image.src, '400x');
          }
        }
      } else if (product) {
        // Show price range or first variant price
        const prices = product.variants.map(v => v.price);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        if (minPrice === maxPrice) {
          priceEl.textContent = this.formatMoney(minPrice);
        } else {
          priceEl.textContent = `${this.formatMoney(minPrice)} - ${this.formatMoney(maxPrice)}`;
        }
      }
    }

    updateQuantity(change) {
      const input = this.querySelector('.f4d-quick-add-modal__qty-input');
      if (!input) return;

      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 99;
      const newValue = Math.max(min, Math.min(max, this.quantity + change));

      this.quantity = newValue;
      input.value = newValue;

      // Update button states
      const decreaseBtn = this.querySelector('.f4d-quick-add-modal__qty-btn--minus');
      const increaseBtn = this.querySelector('.f4d-quick-add-modal__qty-btn--plus');

      if (decreaseBtn) {
        decreaseBtn.disabled = newValue <= min;
      }
      if (increaseBtn) {
        increaseBtn.disabled = newValue >= max;
      }
    }

    async addToCart() {
      if (!this.selectedVariant) return;

      const addBtn = this.querySelector('[data-action="add-to-cart"]');
      if (!addBtn || addBtn.disabled) return;

      // Set loading state
      addBtn.dataset.state = 'loading';
      addBtn.disabled = true;

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: this.selectedVariant.id,
            quantity: this.quantity
          })
        });

        const data = await response.json();

        if (data.status === 422 || data.status === 'error') {
          throw new Error(data.message || 'Could not add to cart');
        }

        // Success
        addBtn.dataset.state = 'success';

        // Update cart count
        await this.updateCartCount();

        // Dispatch event
        this.dispatchCartEvent(data);

        // Close modal after delay
        setTimeout(() => {
          this.close();
        }, 1500);

      } catch (error) {
        console.error('Add to cart error:', error);
        addBtn.dataset.state = '';
        addBtn.disabled = false;
        this.showError(error.message);
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Update cart icon count
        const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count], .header-actions__cart-count');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.classList.remove('hidden');
          }
        });

        // Try to update cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.refresh === 'function') {
          cartDrawer.refresh();
        }
      } catch (e) {
        console.error('Could not update cart count', e);
      }
    }

    dispatchCartEvent(data) {
      document.dispatchEvent(new CustomEvent('cart:add', {
        detail: {
          product_id: this.productData?.id,
          variant_id: this.selectedVariant?.id,
          quantity: this.quantity,
          cart: data
        }
      }));

      if (typeof ThemeEvents !== 'undefined') {
        document.dispatchEvent(new CustomEvent(ThemeEvents?.cartUpdate || 'theme:cart:update', {
          detail: { resource: data }
        }));
      }
    }

    showError(message) {
      const errorEl = this.querySelector('.f4d-quick-add-modal__error');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.dataset.visible = 'true';

        setTimeout(() => {
          errorEl.dataset.visible = 'false';
        }, 5000);
      }
    }

    formatMoney(cents) {
      // Use Shopify's money format if available, otherwise format as EUR
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }

      // Default EUR format
      const euros = (cents / 100).toFixed(2).replace('.', ',');
      return `€${euros}`;
    }

    getSizedImageUrl(url, size) {
      if (!url) return '';

      // Handle Shopify CDN URLs
      if (url.includes('cdn.shopify.com')) {
        return url.replace(/(_\d+x\d+)?(\.[a-zA-Z]+)(\?.*)?$/, `_${size}$2$3`);
      }

      // Append size parameter for other URLs
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}width=${size.replace('x', '')}`;
    }
  }

  // Register custom elements
  if (!customElements.get('f4d-quick-add-inline')) {
    customElements.define('f4d-quick-add-inline', F4DQuickAddInline);
  }

  if (!customElements.get('f4d-quick-add-modal')) {
    customElements.define('f4d-quick-add-modal', F4DQuickAddModal);
  }

})();
